/**
 * Trace-id helpers shared by the error-telemetry path and the UI.
 *
 * Every apiClient request carries a W3C `traceparent` (see client.ts) and the
 * server echoes `traceresponse` (and, from older paths, an `x-trace-id`). When a request fails we
 * want ONE id that is:
 *   - reported to `/api/client-crashes/` (so ops can find the alert), AND
 *   - shown in the inline error UI (so a user's *screenshot* carries the id).
 * Those two must be identical, which is why the router mints/reads the id once
 * and `stampTraceId`s it onto the error for the component to read back.
 *
 * A non-Axios throw escaping a queryFn (`ResponseShapeError` on a 200,
 * `HtmlResponseError`) has no request context, so `traceIdFromError` mints a
 * fresh id. That id still links the screenshot to the ops alert, and the
 * alert's breadcrumbs carry the real request trace-id for the server hop.
 */
import axios from 'axios';
import { newTraceId } from '@/api/clientErrors';
import { parseTraceresponse, requestTraceId } from '@/lib/traceContext';

/** Property key the router stamps the resolved id onto (non-enumerable intent). */
const TRACE_KEY = '__traceId';

/** Second mark (#1685): the id a report was actually SENT under for this error. */
const REPORTED_KEY = '__reportedTraceId';

/** The ONE reader of the legacy `x-trace-id` RESPONSE header (traceHeaders.guard allows only this). */
function responseTraceIdHeader(error: { response?: { headers?: unknown } }): string | undefined {
  const headers = error.response?.headers as Record<string, unknown> | undefined;
  const value = headers?.['x-trace-id'];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readAxiosTraceId(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const tr = error.response?.headers?.['traceresponse'];
  const parsed = parseTraceresponse(typeof tr === 'string' ? tr : undefined);
  if (parsed) return parsed.traceId;
  const resHeader = responseTraceIdHeader(error);
  if (resHeader) return resHeader;
  // #1185 (vingapi#1742): every 5xx body carries `trace_id`, so a proxy that
  // drops the header still yields the server's id.
  const data: unknown = error.response?.data;
  if (data && typeof data === 'object') {
    const bodyId = (data as Record<string, unknown>).trace_id;
    if (typeof bodyId === 'string' && bodyId.length > 0) return bodyId;
  }
  return requestTraceId(error.config);
}

/**
 * The trace id to REPORT for a failed request. Prefers the server's echoed
 * `traceresponse` trace-id, then `x-trace-id`, then the error body's `trace_id`
 * (#1185), then the request's own `traceparent` trace-id, then a fresh id. Always returns a string — the report must never be id-less.
 */
export function traceIdFromError(error: unknown): string {
  return readAxiosTraceId(error) ?? newTraceId();
}

/** Attach the resolved id to the error so the UI can render the same id. */
export function stampTraceId(error: unknown, traceId: string): void {
  if (error && typeof error === 'object') {
    try {
      (error as Record<string, unknown>)[TRACE_KEY] = traceId;
    } catch {
      // Frozen error object — the report still carries the id; the UI just
      // won't be able to show it. Non-fatal.
    }
  }
}

/**
 * Record that a client-crash report carrying `traceId` was SENT for this error
 * (#1685, owner Q-550). Called by `reportClientError` once the report really goes
 * out; a report dropped by the session cap or by signature dedup is not marked.
 */
export function markReported(error: unknown, traceId: string): void {
  if (error && typeof error === 'object') {
    try {
      Object.defineProperty(error, REPORTED_KEY, { value: traceId, enumerable: false, configurable: true, writable: true });
    } catch {
      // Frozen error object: the report is sent, the UI just shows no reference. Non-fatal.
    }
  }
}

/**
 * The id to SHOW in the UI: the stamped id, but only when a report under that
 * exact id was sent. `undefined` means no alert exists for this error, so the
 * UI shows no reference. `getErrorTraceId` stays the unconditional reader
 * (FailureScreen and the copy-report path).
 */
export function getReportedTraceId(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const reported = (error as Record<string, unknown>)[REPORTED_KEY];
  if (typeof reported !== 'string' || reported.length === 0) return undefined;
  return getErrorTraceId(error) === reported ? reported : undefined;
}

/**
 * The trace id to SHOW for a failed request, or `undefined` if none can be
 * shown WITHOUT minting a fresh (uncorrelated) one. Reads the stamped id first
 * (matches the reported id exactly), then falls back to the Axios headers so an
 * AxiosError still shows a correlatable id even if stamping hasn't run yet.
 * Deliberately does NOT mint — a render-time id would not match any alert.
 */
export function getErrorTraceId(error: unknown): string | undefined {
  if (error && typeof error === 'object') {
    const stamped = (error as Record<string, unknown>)[TRACE_KEY];
    if (typeof stamped === 'string' && stamped.length > 0) return stamped;
  }
  return readAxiosTraceId(error);
}
